import {
  AvailabilityDayTemplate,
  AvailabilityMaybeReason,
  AvailabilityQuarterKey,
  AvailabilityStatus,
  AvailabilityTimeBlock,
  AvailabilityWeekTemplate,
} from '../../../types';

export type TeacherAvailabilityDayKey = keyof AvailabilityWeekTemplate;

export type TeacherAvailabilityDayDef = {
  key: TeacherAvailabilityDayKey;
  label: string;
  windowLabel: string;
  start: string;
  end: string;
};

export type TeacherAvailabilityQuarterDef = {
  key: AvailabilityQuarterKey;
  label: string;
  startDate: string;
  endDate: string;
};

export const TEACHER_AVAILABILITY_DAYS: TeacherAvailabilityDayDef[] = [
  { key: 'mon', label: 'Mon', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'tue', label: 'Tue', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'wed', label: 'Wed', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'thu', label: 'Thu', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'fri', label: 'Fri', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'sat', label: 'Sat', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
  { key: 'sun', label: 'Sun', windowLabel: '5:00 AM-11:00 PM', start: '05:00', end: '23:00' },
];

export const TEACHER_AVAILABILITY_QUARTERS: TeacherAvailabilityQuarterDef[] = [
  { key: 'winter2026', label: 'Winter 2026', startDate: '2026-01-01', endDate: '2026-03-31' },
  { key: 'spring2026', label: 'Spring 2026', startDate: '2026-04-01', endDate: '2026-06-30' },
  { key: 'summer2026', label: 'Summer 2026', startDate: '2026-07-01', endDate: '2026-08-31' },
  { key: 'fall2026', label: 'Fall 2026', startDate: '2026-09-01', endDate: '2026-12-31' },
  { key: 'winter2027', label: 'Winter 2027', startDate: '2027-01-01', endDate: '2027-03-31' },
  { key: 'spring2027', label: 'Spring 2027', startDate: '2027-04-01', endDate: '2027-06-30' },
];

export const TEACHER_AVAILABILITY_MAYBE_REASONS: Array<{ value: AvailabilityMaybeReason; label: string }> = [
  { value: 'schedule_conflict', label: 'Schedule conflict' },
  { value: 'travel', label: 'Travel' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'health', label: 'Health' },
  { value: 'other', label: 'Other' },
];

export function cloneAvailability<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

export function minutes(t: string): number {
  const [hh, mm] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return NaN;
  return hh * 60 + mm;
}

export function isValidTime(t: string): boolean {
  const m = minutes(t);
  return Number.isFinite(m) && m >= 0 && m < 24 * 60;
}

export function formatAvailabilityTime(t: string): string {
  if (!isValidTime(t)) return t || '-';
  const [hhRaw, mmRaw] = t.split(':').map(Number);
  const suffix = hhRaw >= 12 ? 'PM' : 'AM';
  const hour = hhRaw % 12 || 12;
  return `${hour}:${String(mmRaw).padStart(2, '0')} ${suffix}`;
}

export function normalizeAvailabilityTimeRange(start: string, end: string): { start: string; end: string } {
  if (!isValidTime(start) || !isValidTime(end)) return { start, end };
  const startM = minutes(start);
  const endM = minutes(end);
  if (startM < endM) return { start, end };

  const [endHourRaw, endMinuteRaw] = end.split(':').map(Number);
  // If someone enters/selects "6" while the visible UI hides AM/PM, treat it as 6 PM
  // when that makes the range valid. This preserves overnight ranges as invalid.
  if (endHourRaw > 0 && endHourRaw < 12) {
    const pmEndM = endM + 12 * 60;
    if (startM < pmEndM) {
      const hh = String(endHourRaw + 12).padStart(2, '0');
      const mm = String(endMinuteRaw || 0).padStart(2, '0');
      return { start, end: `${hh}:${mm}` };
    }
  }

  return { start, end };
}

export function defaultAvailabilityDay(dayKey: TeacherAvailabilityDayKey): AvailabilityDayTemplate {
  const dayDef = TEACHER_AVAILABILITY_DAYS.find(d => d.key === dayKey) || TEACHER_AVAILABILITY_DAYS[0];
  return {
    status: 'unavailable' as AvailabilityStatus,
    maybeReason: null,
    maybeNote: '',
    timeDetailsEnabled: true,
    blocks: [{ start: dayDef.start, end: dayDef.end, status: 'unavailable' as AvailabilityStatus }],
  };
}

export function defaultAvailabilityWeek(): AvailabilityWeekTemplate {
  return {
    sun: defaultAvailabilityDay('sun'),
    mon: defaultAvailabilityDay('mon'),
    tue: defaultAvailabilityDay('tue'),
    wed: defaultAvailabilityDay('wed'),
    thu: defaultAvailabilityDay('thu'),
    fri: defaultAvailabilityDay('fri'),
    sat: defaultAvailabilityDay('sat'),
  };
}

export function normalizeAvailabilityBlocks(blocks: AvailabilityTimeBlock[] | undefined): AvailabilityTimeBlock[] {
  const cleaned = (Array.isArray(blocks) ? blocks : [])
    .filter(b => b && typeof b.start === 'string' && typeof b.end === 'string' && typeof b.status === 'string')
    .map(b => ({ start: b.start, end: b.end, status: b.status as AvailabilityStatus }));
  cleaned.sort((a, b) => minutes(a.start) - minutes(b.start));
  return cleaned;
}

export function firstAvailabilityBlock(day: AvailabilityDayTemplate | undefined, dayKey: TeacherAvailabilityDayKey): AvailabilityTimeBlock {
  const fallback = defaultAvailabilityDay(dayKey).blocks?.[0] as AvailabilityTimeBlock;
  const first = normalizeAvailabilityBlocks(day?.blocks)[0] || {
    start: fallback.start,
    end: fallback.end,
    status: day?.status || fallback.status,
  };
  const range = normalizeAvailabilityTimeRange(first.start, first.end);
  return { ...first, ...range };
}

export function currentAvailabilityQuarterKey(d = new Date()): AvailabilityQuarterKey {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  if (y === 2026 && m >= 4 && m <= 6) return 'spring2026';
  if (y === 2026 && m >= 7 && m <= 8) return 'summer2026';
  if (y === 2026 && m >= 9 && m <= 12) return 'fall2026';
  return 'winter2026';
}

/**
 * Season shown by default when admins plan a teacher's recurring weekly availability.
 * One calendar month after a season starts, planning rolls forward to the next season.
 * Date-specific availability and current-season readiness should continue using
 * currentAvailabilityQuarterKey instead.
 */
export function planningAvailabilityQuarterKey(d = new Date()): AvailabilityQuarterKey {
  const dateKey = [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, '0'),
    String(d.getDate()).padStart(2, '0'),
  ].join('-');

  const currentIndex = TEACHER_AVAILABILITY_QUARTERS.findIndex(
    quarter => dateKey >= quarter.startDate && dateKey <= quarter.endDate
  );
  if (currentIndex < 0) return currentAvailabilityQuarterKey(d);

  const current = TEACHER_AVAILABILITY_QUARTERS[currentIndex];
  const next = TEACHER_AVAILABILITY_QUARTERS[currentIndex + 1];
  if (!next) return current.key;

  const start = new Date(`${current.startDate}T12:00:00`);
  start.setMonth(start.getMonth() + 1);
  const rollForwardKey = [
    start.getFullYear(),
    String(start.getMonth() + 1).padStart(2, '0'),
    String(start.getDate()).padStart(2, '0'),
  ].join('-');

  return dateKey >= rollForwardKey ? next.key : current.key;
}
